/**
 * MarginBreakdownLegend — names every mark actually on the plot, and only those: "No cost
 * basis" and "Loss" appear when something on screen genuinely is one, and the off-scale
 * count appears when a GM% left the axis. Split from the chart to hold its file budget.
 */

import React from 'react'
import { COST_COMPONENTS, type BreakdownBar } from '../../../viewmodels/orderBook'

interface Props {
  bars: BreakdownBar[]
  fills: string[]
  splitCost: boolean
  marginLine: boolean
  clipped: number
}

export default function MarginBreakdownLegend({
  bars, fills, splitCost, marginLine, clipped,
}: Props): React.JSX.Element {
  const entries: [string, string][] = []
  if (splitCost) COST_COMPONENTS.forEach((name, i) => entries.push([name, fills[i]]))
  else entries.push(['Cost', 'var(--orange)'])
  if (marginLine) entries.push(['Margin £', 'var(--green)'])
  // "Costed, but not on this lens" is not "uncosted", and the legend must not say it is.
  const off = bars.find((b) => b.cost === null && b.offLens)?.offLens
  if (off) entries.push([`costed on ${off}, not here`, 'var(--mut2)'])
  if (bars.some((b) => b.cost === null && !b.offLens)) entries.push(['No cost basis', 'var(--mut2)'])
  if (bars.some((b) => b.margin !== null && b.margin < 0)) entries.push(['Loss', 'var(--red)'])
  entries.push(['GM %', 'var(--amber)'])
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 pb-3 pt-1 font-mono text-[10px] text-mut2">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-sm border border-mut bg-mut/20" />
        Value
      </span>
      {entries.map(([name, colour]) => (
        <span key={name} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: colour }} />
          {name}
        </span>
      ))}
      {clipped > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0 w-0"
            style={{ borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '7px solid var(--red)' }}
          />
          {clipped} GM% off the scale — hover for the figure
        </span>
      )}
    </div>
  )
}
